import type { ReactNode } from "react";
import Link from "next/link";
import type { BreadcrumbPathSerializable } from "./breadcrumb.types";
import { buildBreadcrumbJsonLd, destinationKey, getSiteOrigin, isExternalHref } from "./breadcrumb-utils";
import Script from "next/script";

export type { BreadcrumbPathSerializable as BreadcrumbPath } from "./breadcrumb.types";

const textBase =
    "text-xs font-semibold leading-[19.2px] tracking-[0.12px] md:text-md md:leading-[20.8px]";

interface BreadcrumbProps {
    paths: BreadcrumbPathSerializable[];
    /** Canonical absolute URL of the current page (helps JSON-LD when the last crumb has no `route`/`href`). */
    currentPageAbsoluteUrl?: string;
}

const Breadcrumb = ({ paths, currentPageAbsoluteUrl }: BreadcrumbProps) => {
    const siteOrigin = getSiteOrigin();
    const jsonLd = buildBreadcrumbJsonLd(paths, { siteOrigin, currentPageAbsoluteUrl });

    return (
        <nav aria-label="Breadcrumb" className="relative mb-0 w-full py-[10px] md:mb-8">
            <ol className="m-0 flex list-none flex-wrap items-center p-0">
                {paths.map((path, index) => {
                    const isLast = index === paths.length - 1;
                    const dest = destinationKey(path);
                    const isClickable = !!dest;
                    const className = `${textBase} ${
                        isLast
                            ? "text-orange-500"
                            : isClickable
                              ? "text-neutral-300 underline-offset-2 hover:underline"
                              : "text-neutral-900"
                    }`;

                    let inner: ReactNode;
                    if (!isLast && dest) {
                        if (isExternalHref(dest)) {
                            inner = (
                                <a href={dest} className={className}>
                                    {path.label}
                                </a>
                            );
                        } else {
                            inner = (
                                <Link href={dest} className={className}>
                                    {path.label}
                                </Link>
                            );
                        }
                    } else {
                        inner = (
                            <span className={className} {...(isLast ? { "aria-current": "page" as const } : {})}>
                                {path.label}
                            </span>
                        );
                    }

                    return (
                        <li key={index} className="flex items-center">
                            {inner}
                            {index < paths.length - 1 && (
                                <span
                                    className="mx-1 inline-flex items-center leading-[19.2px] text-neutral-900 md:leading-[20.8px]"
                                    aria-hidden
                                >
                                    /
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
            <Script
                id="breadcrumb-schema"
                type="application/ld+json"
                strategy="afterInteractive"
                // eslint-disable-next-line react/no-danger -- JSON-LD must be inline for crawlers
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            />
        </nav>
    );
};

export default Breadcrumb;
